import type { ApiConfig, ApiMessage } from '../api/types';

const DEFAULT_REVOKE_TIMEZONE = 'Asia/Ho_Chi_Minh';
// Shown only by code that renders `content.text` verbatim; the message bubble uses a localized string
const RECALLED_MESSAGE_FALLBACK_TEXT = 'Tin nhắn đã được thu hồi';

// What a recalled message must lose. The stores merge an incoming message over the stored one, so a
// field the server leaves out of the recalled shape would otherwise survive from the original — the
// quoted reply, the forward header or the reactions would keep showing what was taken back.
const RECALLED_CLEARED_FIELDS = [
  'replyInfo', 'forwardInfo', 'reactions', 'reactors', 'inlineButtons', 'keyboardButtons', 'isEdited',
  'editDate', 'groupedId', 'isInAlbum', 'viewsCount', 'forwardsCount', 'isPinned', 'factCheck', 'effectId',
  'hasUnreadMention', 'isMentioned', 'isMediaUnread', 'transcriptionId', 'summaryLanguageCode',
  'isInvertedMedia', 'areReactionsPossible', 'suggestedPostInfo', 'viaBotId',
] as const satisfies readonly (keyof ApiMessage)[];

const dayFormatters = new Map<string, Intl.DateTimeFormat>();

// Fills in everything the recalled shape drops with an explicit `undefined`, keeping what the server
// did send (a forum message keeps its topic-only `replyInfo`)
export function buildRecalledMessageFields(
  message: Partial<ApiMessage>, recalledAt: number,
): Partial<ApiMessage> {
  const fields: Partial<ApiMessage> = {
    isRecalled: true,
    recalledAt: message.recalledAt ?? recalledAt,
    content: { text: { text: RECALLED_MESSAGE_FALLBACK_TEXT, entities: [] } },
  };

  RECALLED_CLEARED_FIELDS.forEach((key) => {
    if (message[key] === undefined) fields[key] = undefined;
  });

  return fields;
}

// The part of a forum message's reply that only places it in its topic, which a recalled message keeps
// so it does not fall out of the topic
export function getRecalledReplyInfo(replyInfo: ApiMessage['replyInfo']): ApiMessage['replyInfo'] {
  if (replyInfo?.type !== 'message' || !replyInfo.isForumTopic) return undefined;

  const topicId = replyInfo.replyToTopId ?? replyInfo.replyToMsgId;
  return {
    type: 'message', replyToMsgId: topicId, replyToTopId: topicId, isForumTopic: true,
  };
}

// Without the server's rules (an older backend, or no config loaded yet) recall is held to the same
// day in the company zone, the safe default the spec sets; the server enforces the real rules anyway.
// "Now" is the server clock from the stored config rather than the device clock, so turning the
// device clock back cannot reopen the window, in any tab and even after an offline reload.
export function getIsInRecallWindow(config: ApiConfig | undefined, messageDate: number, isPrivate: boolean) {
  const now = Date.now() / 1000 + (config?.serverTimeOffset ?? 0);
  const limit = isPrivate ? config?.revokePmTimeLimit : config?.revokeTimeLimit;
  if (config?.revokeWindow === 'duration' && limit !== undefined) {
    return now - messageDate <= limit;
  }

  const formatter = getDayFormatter(config?.revokeTimezone || DEFAULT_REVOKE_TIMEZONE);
  return formatter.format(messageDate * 1000) === formatter.format(now * 1000);
}

function getDayFormatter(timeZone: string) {
  let formatter = dayFormatters.get(timeZone);
  if (formatter) return formatter;

  try {
    formatter = new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' });
  } catch (err) {
    // An unknown zone name from the server must not break the menu; fall back to the company zone
    formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone: DEFAULT_REVOKE_TIMEZONE, year: 'numeric', month: '2-digit', day: '2-digit',
    });
  }

  dayFormatters.set(timeZone, formatter);
  return formatter;
}
